import {switchMap, delay} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as methods from "../constants/methods";
import * as componentTypes from "../constants/componentTypes";
import * as modules from "../constants/modules";
import * as handlers from "../constants/handlers";
import * as settings from "../constants/settings";
import * as filtering from "../constants/filtering";
import * as resources from "../constants/resources";
import * as statsTypes from "../constants/statsTypes";
import * as navigationActions from "../actions/navigationActions";
import * as ticketingActions from "../actions/ticketingActions";
import * as rccActions from "../actions/rccActions";
import * as windowService from "../services/windowService";
import * as keyboardEventService from "../services/keyboardEventService";
import * as searchPanelService from "../services/searchPanelService";
import * as reducerHelper from "../helpers/reducerHelper";
import * as messageHelper from "../helpers/messageHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as tooltipHelper from "../helpers/tooltipHelper";
import * as translationHelper from "../helpers/translationHelper";
import * as getterHelper from "../helpers/getterHelper";

import Tab from "../models/gui/Tab";

const initTabs = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.INIT_USER_SETTINGS),
        switchMap(({payload}) => {
            let tabList = [];
            let initActions = [];

            if (state$.value.module.redlikeSoftware || state$.value.module.colorisSoftware) {
                let tabId = 1;
                tabList.push(
                    new Tab(
                        componentTypes.DEFAULT_TAB_ID,
                        translationHelper.translateTabName(componentTypes.FAVORITES_TAB),
                        componentTypes.FAVORITES_TAB,
                        null,
                        tabId
                    )
                );
                initActions.push({
                    type: actionTypes.FAVORITES_TAB_INIT,
                    payload: {
                        tabId: tabId
                    }
                });

                let initFormulaList = [];

                if (
                    state$.value.login.loggedUser.canRead(resources.SETTINGS_TABS_MEMORY) &&
                    state$.value.settings.tabsMemory
                ) {
                    for (let oTab of windowService.getOpenedTabs()) {
                        if (getterHelper.isFormulaTab(oTab.type)) {
                            initFormulaList.push({
                                color: modelHelper.unserializeColor(oTab.color),
                                product: modelHelper.unserializeProduct(oTab.product),
                                fandeck: modelHelper.unserializeFandeck(oTab.fandeck),
                                base: modelHelper.unserializeBase(oTab.base)
                            });
                        } else if (oTab.type === componentTypes.MATCHING_TAB) {
                            if (state$.value.login.loggedUser.canRead(getterHelper.getResourceByTabType(oTab.type))) {
                                tabId++;
                                initActions.push(
                                    navigationActions.addNewMatchingTab(
                                        tabId,
                                        null,
                                        state$.value.settings.overwriteLastTab,
                                        null
                                    )
                                );
                            }
                        } else {
                            if (
                                state$.value.login.loggedUser.canRead(getterHelper.getResourceByTabType(oTab.type)) &&
                                tabList.some((tab) => tab.type !== oTab.type)
                            ) {
                                tabId++;
                                tabList.push(modelHelper.createNextTabObject(tabId, oTab.type));
                            }
                        }
                    }

                    if (initFormulaList.length > 0) {
                        initActions.push({
                            type: actionTypes.INIT_FORMULA_TAB_LIST,
                            payload: {
                                formulaList: initFormulaList
                            }
                        });
                    }
                }

                if (payload.initialTabType !== componentTypes.FAVORITES_TAB && initFormulaList.length === 0) {
                    tabId++;
                    tabList.push(
                        new Tab(
                            tabId,
                            translationHelper.translateTabName(payload.initialTabType),
                            payload.initialTabType,
                            null,
                            tabId
                        )
                    );
                    initActions.push({
                        type: actionTypes.FORMULA_STORE_INIT,
                        payload: {
                            tabId: tabId,
                            type: payload.initialTabType
                        }
                    });
                }
            }

            if (state$.value.module.redlikeLightSoftware) {
                switch (state$.value.settings.customHomeButton) {
                    case settings.NAV_ITEM_MY_ORDERS:
                        tabList.push(
                            new Tab(
                                componentTypes.DEFAULT_TAB_ID,
                                translationHelper.translateTabName(componentTypes.MY_ORDERS_TAB),
                                componentTypes.MY_ORDERS_TAB,
                                null,
                                1
                            )
                        );
                        break;
                    case settings.NAV_ITEM_ORDERS_HISTORY:
                        tabList.push(
                            new Tab(
                                componentTypes.DEFAULT_TAB_ID,
                                translationHelper.translateTabName(componentTypes.ORDERS_HISTORY_TAB),
                                componentTypes.ORDERS_HISTORY_TAB,
                                null,
                                1
                            )
                        );
                        break;
                    case settings.NAV_ITEM_SPECTRO:
                        tabList.push(
                            new Tab(
                                componentTypes.DEFAULT_TAB_ID,
                                translationHelper.translateTabName(componentTypes.SPECTRO_TAB),
                                componentTypes.SPECTRO_TAB,
                                null,
                                1
                            )
                        );
                        break;
                    case settings.NAV_ITEM_STATISTICS:
                        tabList.push(
                            new Tab(
                                componentTypes.DEFAULT_TAB_ID,
                                translationHelper.translateTabName(componentTypes.STATISTICS_TAB),
                                componentTypes.STATISTICS_TAB,
                                null,
                                1
                            )
                        );
                        break;
                    case settings.NAV_ITEM_TO_DISPENSE:
                        tabList.push(
                            new Tab(
                                componentTypes.DEFAULT_TAB_ID,
                                translationHelper.translateTabName(componentTypes.TO_DISPENSE_TAB),
                                componentTypes.TO_DISPENSE_TAB,
                                null,
                                1
                            )
                        );
                        break;
                    default:
                        tabList.push(
                            new Tab(
                                componentTypes.DEFAULT_TAB_ID,
                                translationHelper.translateTabName(componentTypes.GLOBAL_SEARCH_TAB),
                                componentTypes.GLOBAL_SEARCH_TAB,
                                null,
                                1
                            )
                        );
                        break;
                }
            }

            if (state$.value.module.gammaSoftware) {
                if (state$.value.module.name === modules.MODULE_FRONTDESK) {
                    tabList.push(
                        new Tab(
                            1,
                            translationHelper.translateTabName(componentTypes.MAIN_TAB),
                            componentTypes.MAIN_TAB,
                            null,
                            1,
                            componentTypes.DATABASE_SCENE
                        )
                    );
                    initActions.push({
                        type: actionTypes.FORMULA_STORE_INIT,
                        payload: {
                            tabId: componentTypes.DEFAULT_TAB_ID,
                            type: componentTypes.MAIN_TAB
                        }
                    });
                }

                if (state$.value.module.name === modules.MODULE_MACHINE) {
                    tabList.push(
                        new Tab(
                            componentTypes.DEFAULT_TAB_ID,
                            translationHelper.translateTabName(componentTypes.TO_DISPENSE_TAB),
                            componentTypes.TO_DISPENSE_TAB,
                            null,
                            1
                        )
                    );
                }
            }

            initActions.unshift({
                type: actionTypes.INIT_TAB_LIST,
                payload: {
                    tabList: tabList
                }
            });

            if (payload.customBannerPosition !== null) {
                initActions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_BANNERS
                    },
                    method: handlers.GET_ALL_BANNERS,
                    tabId: 0
                });
            }

            initActions.push({type: actionTypes.MENU_TRANSLATE_KEYWORDS});
            initActions.push({type: actionTypes.SYNC_MACHINE_CONFIGURATION});
            initActions.push({type: actionTypes.SYNC_HELPER_CONFIGURATION});

            return initActions;
        })
    );

const addNewTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.ADD_NEW_TAB),
        switchMap(({payload}) => {
            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            if (getterHelper.isFormulaTab(payload.type)) {
                if (
                    getterHelper.getNumberOfFormulaTabs(state$.value.navigation.tabList) >=
                    state$.value.settings.maxNumberOfTabs
                ) {
                    if (!state$.value.settings.overwriteLastTab) {
                        return [messageHelper.getInfoMessageAction("flashMessage.maxTabs")];
                    }

                    let lastFormulaTab = getterHelper.getLastFormulaTab(state$.value.navigation.tabList);

                    if (lastFormulaTab !== null) {
                        if (
                            state$.value.formula[lastFormulaTab.id] !== undefined &&
                            state$.value.formula[lastFormulaTab.id].dispensing
                        ) {
                            return [
                                {
                                    type: actionTypes.SET_ACTIVE_TAB,
                                    payload: {
                                        tabId: lastFormulaTab.id
                                    }
                                }
                            ];
                        }

                        newTabId = lastFormulaTab.id;
                    }
                }

                return [
                    {
                        type: actionTypes.FORMULA_STORE_INIT,
                        payload: {
                            tabId: newTabId,
                            type: payload.type
                        }
                    },
                    {
                        type: actionTypes.PREPARE_NEW_TAB,
                        payload: {
                            tabId: newTabId,
                            type: payload.type,
                            scene: payload.scene
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SAVE_TAB_OR_OPEN_EXISTING_TAB,
                    payload: {
                        tabId: newTabId,
                        type: payload.type,
                        scene: payload.scene
                    }
                }
            ];
        })
    );

const prepareNewTab = (action$) =>
    action$.pipe(
        ofType(actionTypes.PREPARE_NEW_TAB),
        delay(100),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SAVE_NEW_TAB,
                    payload: {
                        tabId: payload.tabId,
                        type: payload.type,
                        scene: payload.scene
                    }
                }
            ];
        })
    );

const openHomeTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.OPEN_HOME_TAB),
        switchMap(() => {
            switch (state$.value.settings.customHomeButton) {
                case settings.NAV_ITEM_MY_ORDERS:
                    return [{type: actionTypes.MENU_ADD_NEW_MY_ORDERS_TAB}];
                case settings.NAV_ITEM_ORDERS_HISTORY:
                    return [{type: actionTypes.MENU_ADD_NEW_ORDERS_HISTORY_TAB}];
                case settings.NAV_ITEM_SEARCH_NEW_FORMULA:
                    return [{type: actionTypes.MENU_ADD_NEW_GLOBAL_SEARCH_TAB}];
                case settings.NAV_ITEM_SPECTRO:
                    return [{type: actionTypes.MENU_ADD_NEW_SPECTRO_TAB}];
                case settings.NAV_ITEM_STATISTICS:
                    return [{type: actionTypes.MENU_ADD_NEW_STATISTICS_TAB}];
                case settings.NAV_ITEM_TO_DISPENSE:
                    return [{type: actionTypes.MENU_ADD_NEW_TO_DISPENSE_TAB}];
                default:
                    return [{type: actionTypes.MENU_ADD_NEW_GLOBAL_SEARCH_TAB}];
            }
        })
    );

const autoRunSpectroMeasurement = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MENU_ADD_NEW_SPECTRO_TAB),
        switchMap(() => {
            if (!state$.value.rcc.connected || !state$.value.spectro.spectro.isSelected) {
                return [];
            }

            if (!state$.value.spectro.spectro.isConnected) {
                return [rccActions.connectSpectro()];
            }

            if (state$.value.module.gammaSoftware) {
                return [rccActions.startMeasureSpectro()];
            }

            return [];
        })
    );

const setAutomaticMainTabScene = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SET_AUTOMATIC_MAIN_TAB_SCENE),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.SET_ACTIVE_TAB_SCENE,
                    payload: {
                        tabId: payload.tabId,
                        scene: searchPanelService.applySmartFocusForScene(
                            state$.value.formula[payload.tabId],
                            state$.value.settings.searchPanelOrder
                        )
                    }
                }
            ];
        })
    );

const loadFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.NAVIGATION_SELECT_FORMULA),
        switchMap(({payload}) => {
            let newTabId =
                payload.tabId !== null ? payload.tabId : reducerHelper.getNewTabId(state$.value.navigation.tabList);

            if (
                getterHelper.getNumberOfFormulaTabs(state$.value.navigation.tabList) >=
                state$.value.settings.maxNumberOfTabs
            ) {
                if (!state$.value.settings.overwriteLastTab) {
                    return [messageHelper.getInfoMessageAction("flashMessage.maxTabs")];
                }

                let lastFormulaTab = getterHelper.getLastFormulaTab(state$.value.navigation.tabList);

                if (lastFormulaTab !== null) {
                    if (
                        state$.value.formula[lastFormulaTab.id] !== undefined &&
                        state$.value.formula[lastFormulaTab.id].dispensing
                    ) {
                        return [
                            {
                                type: actionTypes.SET_ACTIVE_TAB,
                                payload: {
                                    tabId: lastFormulaTab.id
                                }
                            }
                        ];
                    }

                    newTabId = lastFormulaTab.id;
                }
            }

            return [
                {
                    type: actionTypes.NAVIGATION_SAVE_FORMULA,
                    payload: {
                        tabId: newTabId,
                        product: payload.product,
                        fandeck: payload.fandeck,
                        color: payload.color,
                        base: payload.base,
                        bipp: null
                    }
                }
            ];
        })
    );

const navigationSaveFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.NAVIGATION_SAVE_FORMULA),
        switchMap(({payload}) => {
            if (searchPanelService.isSearchPanelValid(state$.value.formula[payload.tabId])) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_CALCULATED_FORMULA,
                            params: {
                                productId: state$.value.formula[payload.tabId].product.id,
                                fandeckId: state$.value.formula[payload.tabId].fandeck.id,
                                colorId: state$.value.formula[payload.tabId].color.id,
                                baseId:
                                    !state$.value.settings.searchPanelBase ||
                                    state$.value.formula[payload.tabId].base === null
                                        ? null
                                        : state$.value.formula[payload.tabId].base.id,
                                baseInProductPackageId: payload.bipp === null ? null : payload.bipp.id,
                                isLocal: state$.value.formula[payload.tabId].color.local
                            }
                        },
                        method: handlers.NAVIGATION_GET_CALCULATED_FORMULA,
                        tabId: payload.tabId
                    }
                ];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_AFTER_SAVE_FORMULA_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];
        })
    );

const saveFormulaDataForNewTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.NAVIGATION_AFTER_SAVE_FORMULA_DATA),
        switchMap(({payload}) => {
            let requests = [];

            let blendedColor = null;

            if (state$.value.formula[payload.tabId].color !== null) {
                blendedColor = state$.value.formula[payload.tabId].color.updateLongDisplayName(
                    state$.value.settings.blendFormulaAndCode,
                    state$.value.settings.formulaCodePrimary
                );
            }

            requests.push(
                {
                    type: actionTypes.CONSTRUCT_SEARCH_PANEL,
                    payload: {
                        tabId: payload.tabId,
                        product: state$.value.formula[payload.tabId].product,
                        fandeck: state$.value.formula[payload.tabId].fandeck,
                        color: blendedColor,
                        base: state$.value.formula[payload.tabId].base,
                        bipp: state$.value.formula[payload.tabId].bipp,
                        bippList: state$.value.formula[payload.tabId].packageList
                    }
                },
                {
                    type: actionTypes.COLORANT_PANEL_INIT,
                    payload: {
                        tabId: payload.tabId,
                        type: componentTypes.MAIN_TAB
                    }
                },
                {
                    type: actionTypes.ADD_NEW_MAIN_TAB,
                    payload: {
                        tabId: payload.tabId,
                        color: blendedColor,
                        scene: state$.value.module.gammaSoftware
                            ? componentTypes.BIPP_SCENE
                            : componentTypes.PACKAGE_SCENE
                    }
                }
            );

            let productName = null;
            let baseName = null;
            let packageName = null;

            if (state$.value.formula[payload.tabId] !== undefined) {
                if (state$.value.formula[payload.tabId].product !== null) {
                    productName = state$.value.formula[payload.tabId].product.name;
                }

                if (state$.value.formula[payload.tabId].base !== null) {
                    baseName = state$.value.formula[payload.tabId].base.name;
                }

                if (state$.value.formula[payload.tabId].package !== null) {
                    packageName = state$.value.formula[payload.tabId].package.package.name;
                }
            }

            if (productName === null || baseName === null || packageName === null) {
                return requests;
            }

            requests.push({
                type: actionTypes.SERVER_REQUEST,
                payload: {
                    type: methods.METHOD_GET_PRODUCT_IMAGE,
                    params: {
                        productName: productName,
                        baseName: baseName,
                        packageName: packageName,
                        online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                    }
                },
                method: handlers.GET_PRODUCT_IMAGE,
                tabId: payload.tabId
            });

            return requests;
        })
    );

const saveFormulaAsCustom = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FORMULA_AS_CUSTOM),
        switchMap(({payload}) => {
            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            return [
                {
                    type: actionTypes.CLONE_MAIN_TO_CUSTOM_TAB,
                    payload: {
                        tabId: newTabId,
                        mainTabId: payload.tabId
                    }
                }
            ];
        })
    );

const saveMatchingFormulaAsCustom = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_MATCHING_FORMULA_AS_CUSTOM),
        switchMap(({payload}) => {
            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            return [
                {
                    type: actionTypes.CLONE_MATCHING_TO_CUSTOM_TAB,
                    payload: {
                        tabId: newTabId,
                        matchingTabId: payload.tabId,
                        tabList: state$.value.navigation.tabList
                    }
                }
            ];
        })
    );

const saveHistoryFormulaAsCustom = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_HISTORY_FORMULA_AS_CUSTOM),
        switchMap(({payload}) => {
            let lastCustomFormulaTab = reducerHelper.getLastTabByType(
                state$.value.navigation.tabList,
                componentTypes.CUSTOM_TAB
            );

            let newTabId = null;

            if (lastCustomFormulaTab === null) {
                newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);
            } else {
                newTabId = lastCustomFormulaTab.id;
            }

            return [
                {
                    type: actionTypes.CLONE_HISTORY_TO_CUSTOM_TAB,
                    payload: {
                        tabId: newTabId,
                        statisticsTabId: payload.tabId,
                        statistics: payload.statistics
                    }
                }
            ];
        })
    );

const closeRedlikeWindow = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.NAVIGATION_CLOSE_REDLIKE_WINDOW),
        switchMap(() => {
            windowService.logMessage("Redlike is closing.");

            if (
                state$.value.login.loggedUser.canRead(resources.SETTINGS_TABS_MEMORY) &&
                state$.value.settings.tabsMemory
            ) {
                windowService.saveOpenedTabs(state$.value.navigation.tabList, state$.value.formula);
            }

            setTimeout(function () {
                windowService.logMessage("Redlike is closed.");
                windowService.closeWindow();
            }, 500);

            if (state$.value.rcc.connected) {
                return [
                    {
                        type: actionTypes.RCC_DISCONNECT
                    }
                ];
            }

            return [];
        })
    );

const openNewMainTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.OPEN_NEW_MAIN_TAB),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            if (!payload.reset && state$.value.formula[payload.tabId].statisticsId !== null) {
                let statisticsId = state$.value.formula[payload.tabId].statisticsId;

                let tab;
                for (tab of state$.value.navigation.tabList) {
                    if (
                        tab.id !== payload.tabId &&
                        state$.value.formula[tab.id] !== undefined &&
                        state$.value.formula[tab.id].statisticsId === statisticsId &&
                        state$.value.formula[tab.id].formula !== null &&
                        state$.value.formula[tab.id].formula.isCorrection() === payload.manualCorrection
                    ) {
                        return [
                            {
                                type: actionTypes.SET_ACTIVE_TAB,
                                payload: {
                                    tabId: tab.id
                                }
                            }
                        ];
                    }
                }
            }

            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            if (
                getterHelper.getNumberOfFormulaTabs(state$.value.navigation.tabList) >=
                state$.value.settings.maxNumberOfTabs
            ) {
                if (!state$.value.settings.overwriteLastTab) {
                    return [messageHelper.getInfoMessageAction("flashMessage.maxTabs")];
                }

                let lastFormulaTab = getterHelper.getLastFormulaTab(state$.value.navigation.tabList);

                if (lastFormulaTab !== null) {
                    if (
                        state$.value.formula[lastFormulaTab.id] !== undefined &&
                        state$.value.formula[lastFormulaTab.id].dispensing
                    ) {
                        return [
                            {
                                type: actionTypes.SET_ACTIVE_TAB,
                                payload: {
                                    tabId: lastFormulaTab.id
                                }
                            }
                        ];
                    }

                    newTabId = lastFormulaTab.id;
                }
            }

            let blendedColor = null;

            if (state$.value.formula[payload.tabId].color !== null) {
                blendedColor = state$.value.formula[payload.tabId].color.updateLongDisplayName(
                    state$.value.settings.blendFormulaAndCode,
                    state$.value.settings.formulaCodePrimary
                );
            }

            let formula = null;
            if (state$.value.formula[payload.tabId].formula !== null) {
                if (payload.manualCorrection) {
                    formula = state$.value.formula[payload.tabId].formula.updateStatsType(statsTypes.MANUAL_CORRECTION);
                    formula.colorantList = formula.realColorantList;
                    formula.colorantListG = formula.realColorantListG;
                    formula.colorantListShot = formula.realColorantListShot;
                    formula.setCorrections();
                    formula.resetScaling();
                } else if (payload.reset || state$.value.formula[payload.tabId].formula.isCorrection()) {
                    formula = state$.value.formula[payload.tabId].formula.updateStatsType(null);
                } else {
                    formula = state$.value.formula[payload.tabId].formula;
                }
            }

            let loadFormulaActions = [
                {
                    type: payload.reset ? actionTypes.CLONE_FORMULA_DATA_AND_RESET : actionTypes.CLONE_FORMULA_DATA,
                    payload: {
                        tabId: payload.tabId,
                        newTabId: newTabId,
                        formula: formula,
                        packageCount: payload.packageCount
                    }
                },
                {
                    type: actionTypes.CONSTRUCT_SEARCH_PANEL,
                    payload: {
                        tabId: newTabId,
                        product: state$.value.formula[payload.tabId].product,
                        fandeck: state$.value.formula[payload.tabId].fandeck,
                        color: blendedColor,
                        base: state$.value.formula[payload.tabId].base,
                        bipp: state$.value.formula[payload.tabId].bipp,
                        bippList: state$.value.formula[payload.tabId].packageList
                    }
                },
                {
                    type: actionTypes.COLORANT_PANEL_INIT,
                    payload: {
                        tabId: newTabId,
                        type: componentTypes.FAVORITES_TAB
                    }
                },
                {
                    type: actionTypes.ADD_NEW_MAIN_TAB,
                    payload: {
                        tabId: newTabId,
                        color: blendedColor,
                        scene: componentTypes.SUMMARY_SCENE
                    }
                },
                {
                    type: actionTypes.MAIN_TAB_RESET_PRICE_LIST,
                    payload: {
                        tabId: newTabId
                    }
                }
            ];

            if (payload.colorStatsId !== null) {
                loadFormulaActions.push({
                    type: actionTypes.MACHINE_DISPENSE_HISTORY_FORMULA,
                    payload: {
                        colorStatsId: payload.colorStatsId,
                        tabId: newTabId
                    }
                });
            }

            if (payload.manualCorrection && formula !== null) {
                loadFormulaActions.push(
                    navigationActions.openModalWithParams(newTabId, componentTypes.EDIT_COLORANTS_MODAL, {
                        formula: formula.updateFormulaBeforeEditing()
                    })
                );
            }

            loadFormulaActions.push({
                type: actionTypes.RELOAD_FORMULA_META_DATA,
                payload: {
                    tabId: newTabId
                }
            });

            if (formula !== null) {
                loadFormulaActions.push({
                    type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                    payload: {
                        tabId: newTabId,
                        formula: formula.getColorantVolumes(),
                        numCanDone: payload.reset ? 0 : state$.value.formula[payload.tabId].numCanDone,
                        numCanTotal:
                            payload.packageCount === null
                                ? state$.value.formula[payload.tabId].numCanTotal
                                : payload.packageCount,
                        customCan: state$.value.formula[payload.tabId].package?.isCustom() ?? false
                    }
                });
            }

            return loadFormulaActions;
        })
    );

const processKeyboardEvent = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.PROCESS_KEYBOARD_EVENT),
        switchMap(({payload}) => {
            try {
                return keyboardEventService.processKeyboardEvent(payload.keyboardEvent, state$.value);
            } catch (e) {
                return [];
            }
        })
    );

const openHelpModal = (action$) =>
    action$.pipe(
        ofType(actionTypes.NAVIGATION_OPEN_HELP_MODAL),
        switchMap(() => {
            const helpMessage = windowService.getHelpMessage();

            if (helpMessage === "pdfAvailable") {
                windowService.openHelpPdfWindow();
                return [];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: componentTypes.MODAL_NO_TAB,
                        type: componentTypes.HELP_MODAL,
                        params: {
                            message: helpMessage
                        }
                    }
                }
            ];
        })
    );

const openModal = (action$) =>
    action$.pipe(
        ofType(actionTypes.NAVIGATION_OPEN_JUST_ONE_MODAL),
        switchMap(({payload}) => {
            if (payload.type === componentTypes.CHANGE_DATABASE_MODAL) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_DATABASES_READY_FOR_PARTIAL_UPDATES
                        },
                        method: handlers.GET_DATABASES_READY_FOR_PARTIAL_UPDATES,
                        tabId: payload.tabId
                    }
                ];
            }

            return [];
        })
    );

const destroyAllTooltips = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_ACTIVE_HELP),
        switchMap(() => {
            if (!state$.value.navigation.activeHelp) {
                tooltipHelper.destroyAll();
            }

            return [];
        })
    );

const imageStorageUpdate = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.IMAGE_STORE_UPDATE),
        switchMap(() => {
            let imageList = [];
            let fandeckImageList = [];
            let cifImageList = [];
            let productFileImageList = [];

            for (let tabId in state$.value.formula) {
                if (state$.value.formula[tabId] !== undefined) {
                    if (state$.value.formula[tabId].package !== null) {
                        imageList.push(state$.value.formula[tabId].package.package.imageId);
                    }
                    if (state$.value.formula[tabId].fandeck !== null) {
                        fandeckImageList.push(state$.value.formula[tabId].fandeck.id);

                        if (state$.value.formula[tabId].color !== null) {
                            cifImageList.push(
                                state$.value.formula[tabId].fandeck.id + "-" + state$.value.formula[tabId].color.id
                            );
                        }
                    }
                    for (let productFile of state$.value.formula[tabId].productFileList) {
                        productFileImageList.push(productFile.id);
                    }
                }
            }

            if (state$.value.toDispense.activeOrder !== null && state$.value.toDispense.activeOrder.package !== null) {
                imageList.push(state$.value.toDispense.activeOrder.package.imageId);
            }

            if (state$.value.rcc.machine.productImageId > 0) {
                imageList.push(state$.value.rcc.machine.productImageId);
            }

            window.imageStore.cleanImageList(
                imageList,
                cifImageList,
                fandeckImageList,
                productFileImageList,
                state$.value.navigation.activeTabType === componentTypes.COLOR_TAB,
                state$.value.barcode.item !== null
            );

            return [];
        })
    );

const setOverrideMode = (action$) =>
    action$.pipe(
        ofType(actionTypes.INTERGAMMA_SET_OVERRIDE_MODE),
        switchMap(({payload}) => {
            if (payload.active) {
                return [
                    {
                        type: actionTypes.MENU_ADD_NEW_OR_RESET_MAIN_TAB,
                        payload: {
                            scene: componentTypes.DATABASE_SCENE
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SET_ACTIVE_TAB,
                    payload: {
                        tabId: componentTypes.DEFAULT_TAB_ID
                    }
                }
            ];
        })
    );

const openOrResetMainTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MENU_ADD_NEW_OR_RESET_MAIN_TAB),
        switchMap(({payload}) => {
            let mainTab = state$.value.navigation.tabList.find((tab) => tab.type === componentTypes.MAIN_TAB);

            if (mainTab === undefined) {
                return [
                    {
                        type: actionTypes.MENU_ADD_NEW_OR_OPEN_MAIN_TAB
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SEARCH_PANEL_INIT,
                    payload: {
                        tabId: mainTab.id,
                        type: componentTypes.MAIN_TAB,
                        local: false
                    }
                },
                {
                    type: actionTypes.SET_ACTIVE_TAB_AND_SCENE,
                    payload: {
                        tabId: mainTab.id,
                        scene: payload.scene
                    }
                }
            ];
        })
    );

const openCustomerHistoryTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.OPEN_CUSTOMER_HISTORY_TAB),
        switchMap(({payload}) => {
            let statisticsTab = reducerHelper.getTabByType(
                state$.value.navigation.tabList,
                componentTypes.STATISTICS_TAB
            );

            if (statisticsTab !== null) {
                return [
                    {
                        type: actionTypes.FILTERING_SAVE_PARAMS,
                        payload: {
                            tabId: statisticsTab.id,
                            paramType: filtering.CUSTOMER,
                            paramValue: [payload.customer]
                        }
                    },
                    {
                        type: actionTypes.SET_ACTIVE_TAB,
                        payload: {
                            tabId: statisticsTab.id
                        }
                    }
                ];
            }

            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            return [
                {
                    type: actionTypes.STATISTICS_TAB_INIT,
                    payload: {
                        tabId: newTabId,
                        dateFilter: false,
                        customerList: [payload.customer]
                    }
                },
                {
                    type: actionTypes.PREPARE_NEW_TAB,
                    payload: {
                        tabId: newTabId,
                        type: componentTypes.STATISTICS_TAB,
                        scene: null
                    }
                }
            ];
        })
    );

const saveTabsAfterClosedActiveTab = (action$) =>
    action$.pipe(
        ofType(actionTypes.CLOSE_ACTIVE_TAB),
        switchMap(() => {
            return [{type: actionTypes.STORE_OPENED_TAB_LIST}];
        })
    );

const storeOpenedTabList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.STORE_OPENED_TAB_LIST),
        switchMap(() => {
            if (
                state$.value.login.loggedUser.canRead(resources.SETTINGS_TABS_MEMORY) &&
                state$.value.settings.tabsMemory
            ) {
                windowService.saveOpenedTabs(state$.value.navigation.tabList, state$.value.formula);
            }

            return [];
        })
    );

const ticketingTabInit = (action$) =>
    action$.pipe(
        ofType(actionTypes.TICKETING_TAB_INIT),
        switchMap(({payload}) => {
            return [ticketingActions.getTickets(payload.tabId), ticketingActions.tmcAvailable()];
        })
    );

const initFormulaTabList = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.INIT_FORMULA_TAB_LIST),
        delay(700),
        switchMap(({payload}) => {
            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            let selectFormulaActions = [];

            for (let formula of payload.formulaList) {
                if (formula.product !== null && formula.fandeck !== null && formula.color !== null) {
                    selectFormulaActions.push({
                        type: actionTypes.NAVIGATION_SELECT_FORMULA,
                        payload: {
                            tabId: newTabId,
                            color: formula.color,
                            product: formula.product,
                            fandeck: formula.fandeck,
                            base: formula.base
                        }
                    });

                    windowService.logInfo(
                        "Opening recently opened formula '" + formula.color.displayName + "' in new tab."
                    );
                }

                newTabId++;
            }

            return selectFormulaActions;
        })
    );

const searchPanelQuickSelect = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SELECT_FORMULA_NAME_AND_CODE, actionTypes.SELECT_FANDECK, actionTypes.SELECT_PRODUCT),
        switchMap(({payload}) => {
            if (state$.value.module.gammaSoftware && state$.value.settings.searchPanelQuickSelect) {
                return [
                    {
                        type: actionTypes.SET_AUTOMATIC_MAIN_TAB_SCENE,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            return [];
        })
    );

const searchPanelSelectHeight = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SET_RENDER, actionTypes.WINDOW_RESIZED),
        delay(200),
        switchMap(() => {
            if (!state$.value.navigation.rendered || !state$.value.settings.searchPanelDropDownCalculate) {
                return [];
            }

            for (let tab of state$.value.navigation.tabList) {
                if (getterHelper.isFormulaTab(tab.type) && state$.value.searchPanel[tab.id] !== undefined) {
                    searchPanelService.updateSelectBoxHeight(
                        tab.id,
                        state$.value.searchPanel[tab.id],
                        state$.value.settings.searchPanelOrder
                    );
                }
            }

            return [];
        })
    );

const initialPartialUpdatesCheck = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SOFTWARE_INITIALIZED),
        switchMap(() => {
            if (state$.value.login.loggedUser.canNotRead(resources.COMPONENT_PARTIAL_UPDATES_MODAL)) {
                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PREPARED_PARTIAL_UPDATES_COUNT
                    },
                    method: handlers.GET_PREPARED_PARTIAL_UPDATES_COUNT_AFTER_INIT,
                    tabId: 0
                }
            ];
        })
    );

export default [
    initTabs,
    addNewTab,
    prepareNewTab,
    openHomeTab,
    autoRunSpectroMeasurement,
    setAutomaticMainTabScene,
    loadFormula,
    navigationSaveFormula,
    saveFormulaDataForNewTab,
    saveFormulaAsCustom,
    saveMatchingFormulaAsCustom,
    saveHistoryFormulaAsCustom,
    closeRedlikeWindow,
    openNewMainTab,
    processKeyboardEvent,
    openHelpModal,
    openModal,
    destroyAllTooltips,
    imageStorageUpdate,
    setOverrideMode,
    openOrResetMainTab,
    openCustomerHistoryTab,
    saveTabsAfterClosedActiveTab,
    storeOpenedTabList,
    initFormulaTabList,
    ticketingTabInit,
    searchPanelQuickSelect,
    searchPanelSelectHeight,
    initialPartialUpdatesCheck
];
